import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { format, parse } from 'date-fns'
import { AlertCircle, Loader2, Phone } from 'lucide-react'
import * as z from 'zod'

import { DaycareBanner } from '@/components/DaycareBanner'
import { useReservations } from '@/components/ReservationContext.tsx'
import { PeluqueriaAvailabilityCalendar } from '@/components/peluqueria-availability-calendar.tsx'
import { PetDetailsForm } from '@/components/pet-details-form'
import { petFormSchema } from '@/features/booking/types/booking.types'
import { ClientDetails } from '@/features/booking/ui/BookingSteps/ClientDetails'
import { useClientProfile } from '@/hooks/use-client-profile'
import { useAuth } from '@/shared/auth'
import { useGlobalPublicConfig } from '@/shared/hooks/use-global-config'
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/alert.tsx'
import { Button } from '@/shared/ui/button.tsx'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card.tsx'
import { Checkbox } from '@/shared/ui/checkbox'
import { DriverServiceSection } from '@/shared/ui/driver-service-section'
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/shared/ui/form.tsx'
import { Label } from '@/shared/ui/label'
import { LanguageSwitchButton } from '@/shared/ui/language-switch-button'
import { zodResolver } from '@hookform/resolvers/zod'
import type { HairSalonReservation } from '@monorepo/functions/src/types/reservations'
import { type DriverService, type HairdressingServiceType } from '@monorepo/functions/src/types/services'

// Función para parsear dirección concatenada
function parseAddress(address: string | undefined): {
    street: string
    streetNumber: string
    city: string
    postalCode: string
    province: string
    country: string
} {
    if (!address || !address.trim()) {
        return { street: '', streetNumber: '', city: '', postalCode: '', province: '', country: '' }
    }

    const parts = address
        .split(',')
        .map(part => part.trim())
        .filter(Boolean)

    if (parts.length >= 6) {
        return {
            street: parts[0] || '',
            streetNumber: parts[1] || '',
            city: parts[2] || '',
            postalCode: parts[3] || '',
            province: parts[4] || '',
            country: parts[5] || '',
        }
    }

    return { street: '', streetNumber: '', city: '', postalCode: '', province: '', country: '' }
}

type HairdressingService = {
    value: HairdressingServiceType
    translationKey: string
}

const HAIRDRESSING_SERVICES: HairdressingService[] = [
    { value: 'bath_and_brush', translationKey: 'booking.step3.petServices.hairdressing.bathAndBrush' },
    { value: 'bath_and_trim', translationKey: 'booking.step3.petServices.hairdressing.bathAndTrim' },
    { value: 'stripping', translationKey: 'booking.step3.petServices.hairdressing.stripping' },
    { value: 'deshedding', translationKey: 'booking.step3.petServices.hairdressing.deshedding' },
    { value: 'brushing', translationKey: 'booking.step3.petServices.hairdressing.brushing' },
    { value: 'spa', translationKey: 'booking.step3.petServices.hairdressing.spa' },
    { value: 'spa_ozone', translationKey: 'booking.step3.petServices.hairdressing.spaOzone' },
    { value: 'knots', translationKey: 'booking.step3.petServices.hairdressing.knots' },
    { value: 'nail_trimming', translationKey: 'booking.step3.petServices.hairdressing.nailTrimming' },
] as const

const formSchema = z.object({
    date: z.date({
        required_error: 'peluqueria.booking.errors.date.required',
    }),
    time: z.string().min(1, 'peluqueria.booking.errors.time.required'),
    services: z.array(z.string()).min(1, 'peluqueria.booking.errors.services.required'),
    clientName: z.string().min(1, { message: 'booking.step4.clientName.required' }),
    clientLastName: z.string().min(1, { message: 'booking.step4.clientLastName.required' }),
    clientEmail: z.string().email({ message: 'booking.step4.clientEmail.invalid' }),
    clientPhone: z.string().regex(/^(?:\+)?[0-9]{1,}$/, { message: 'booking.step4.clientPhone.invalid' }),
    clientAddress: z.string().optional(),
    clientStreet: z.string().optional(),
    clientStreetNumber: z.string().optional(),
    clientCity: z.string().optional(),
    clientPostalCode: z.string().optional(),
    clientProvince: z.string().optional(),
    clientCountry: z.string().optional(),
    pet: petFormSchema,
})

type FormValues = z.infer<typeof formSchema>

interface ServicesSectionProps {
    form: ReturnType<typeof useForm<FormValues>>
    driverService: DriverService | undefined
    onDriverServiceChange: (driverService: DriverService | undefined) => void
    checkInTime: string
    onCheckInTimeChange: (time: string) => void
    selectedDate?: Date
}

const ServicesSection = ({
    form,
    driverService,
    onDriverServiceChange,
    checkInTime,
    onCheckInTimeChange,
    selectedDate,
}: ServicesSectionProps) => {
    const { t } = useTranslation()

    return (
        <Card>
            <CardHeader>
                <CardTitle>{t('peluqueria.booking.services.title', 'Servicios')}</CardTitle>
            </CardHeader>
            <CardContent className='space-y-6'>
                <div className='space-y-4'>
                    <div className='space-y-4'>
                        <Label className='text-base font-medium'>
                            {t('peluqueria.booking.services.hairdressing', 'Servicios de peluquería')}
                        </Label>
                        <FormField
                            control={form.control}
                            name='services'
                            render={({ field }) => (
                                <FormItem>
                                    <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
                                        {HAIRDRESSING_SERVICES.map(service => (
                                            <div key={service.value} className='flex items-center space-x-2'>
                                                <Checkbox
                                                    id={`service-${service.value}`}
                                                    checked={field.value?.includes(service.value)}
                                                    onCheckedChange={checked => {
                                                        const updatedValue = checked
                                                            ? [...(field.value || []), service.value]
                                                            : (field.value || []).filter(
                                                                  (value: string) => value !== service.value,
                                                              )
                                                        field.onChange(updatedValue)
                                                    }}
                                                />
                                                <Label
                                                    htmlFor={`service-${service.value}`}
                                                    className='text-sm sm:text-base'
                                                >
                                                    {t(service.translationKey)}
                                                </Label>
                                            </div>
                                        ))}
                                    </div>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                </div>

                <div className='border-t pt-6'>
                    <h3 className='mb-4 text-xl font-bold'>
                        {t('peluqueria.booking.calendar.title', '¿Cuándo traes a tu mascota?')}
                    </h3>

                    <DriverServiceSection
                        driverService={driverService}
                        onDriverServiceChange={onDriverServiceChange}
                        checkInTime={checkInTime}
                        onCheckInTimeChange={onCheckInTimeChange}
                        selectedDates={selectedDate ? { from: selectedDate, to: selectedDate } : null}
                        showCheckOutTime={false}
                    />

                    <DaycareBanner />
                </div>
            </CardContent>
        </Card>
    )
}

interface SubmitSectionProps {
    formError: string
    hasBackendError: boolean
    isSubmitting: boolean
    globalConfig?: { phoneNumber: string }
}

const SubmitSection = ({ formError, hasBackendError, isSubmitting, globalConfig }: SubmitSectionProps) => {
    const { t } = useTranslation()

    return (
        <>
            {formError && (
                <Alert variant={hasBackendError ? 'destructive' : 'default'}>
                    <AlertCircle className='h-4 w-4' />
                    <AlertTitle>
                        {hasBackendError
                            ? t('booking.errors.backend.title', 'Error del servidor')
                            : t('booking.errors.form.title', 'Faltan campos obligatorios')}
                    </AlertTitle>
                    <AlertDescription className='text-sm'>{formError}</AlertDescription>
                </Alert>
            )}

            <div className='flex flex-col items-center space-y-4'>
                <Button type='submit' className='w-full' disabled={isSubmitting}>
                    {isSubmitting ? (
                        <>
                            <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                            {t('booking.navigation.confirming', 'Confirmando...')}
                        </>
                    ) : (
                        t('booking.navigation.confirm', 'Confirmar reserva')
                    )}
                </Button>

                {globalConfig?.phoneNumber && (
                    <div className='text-center text-sm text-muted-foreground'>
                        <p>{t('booking.needHelp', '¿Necesitas ayuda?')}</p>
                        <a
                            href={`tel:${globalConfig.phoneNumber}`}
                            className='inline-flex items-center text-blue-600 hover:text-blue-800'
                        >
                            <Phone className='mr-1 h-4 w-4' />
                            {globalConfig.phoneNumber}
                        </a>
                    </div>
                )}
            </div>
        </>
    )
}

export default function HairSalonBookingPage() {
    const { t, i18n } = useTranslation()
    const navigate = useNavigate()
    const [dateTimeError, setDateTimeError] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [hasBackendError, setHasBackendError] = useState(false)
    const [formError, setFormError] = useState('')
    const [driverService, setDriverService] = useState<DriverService | undefined>(undefined)
    const { data: globalConfig } = useGlobalPublicConfig()
    const { user } = useAuth()
    const { addReservation } = useReservations()

    const [searchParams] = useSearchParams()
    const userId = searchParams.get('userId') || user?.uid
    const petIndexParam = searchParams.get('petIndex')
    const petIndex = petIndexParam !== null ? parseInt(petIndexParam) : 0
    const { clientProfile } = useClientProfile(userId)
    const [checkInTime, setCheckInTime] = useState<string>(searchParams?.get('time') || '11:00')

    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            services: [],
            clientName: clientProfile?.name || '',
            clientLastName: clientProfile?.lastName || '',
            clientPhone: clientProfile?.phone || '',
            clientEmail: clientProfile?.email || '',
            clientAddress: '',
            clientStreet: '',
            clientStreetNumber: '',
            clientCity: '',
            clientPostalCode: '',
            clientProvince: '',
            clientCountry: '',
            pet: {
                name: '',
                breed: '',
                weight: undefined,
                age: '',
                isNeutered: false,
                personality: '',
                size: 'pequeño',
                sex: 'M',
            },
            date: searchParams?.get('date') ? parse(searchParams.get('date')!, 'yyyy-MM-dd', new Date()) : undefined,
            time: checkInTime,
        },
    })

    // Subscribe to form errors
    useEffect(() => {
        const subscription = form.watch(() => {
            const { errors } = form.formState
            if (Object.keys(errors).length > 0) {
                const firstError = Object.values(errors)[0]

                if (firstError?.message) {
                    setFormError(t(firstError.message as string))
                } else {
                    // Handle specific field errors
                    if (errors.pet) {
                        setFormError(
                            t(
                                'booking.errors.petFields',
                                'Por favor, completa todos los campos de la mascota antes de continuar.',
                            ),
                        )
                    } else if (errors.date || errors.time) {
                        setFormError(
                            t(
                                'peluqueria.booking.errors.dateTime.required',
                                'Por favor, selecciona una fecha y hora para el servicio.',
                            ),
                        )
                    } else if (errors.services) {
                        setFormError(
                            t(
                                'peluqueria.booking.errors.services.required',
                                'Por favor, selecciona al menos un servicio.',
                            ),
                        )
                    } else if (errors.clientName || errors.clientLastName || errors.clientEmail || errors.clientPhone) {
                        setFormError(
                            t(
                                'booking.errors.contactInfo',
                                'Por favor, revisa tus datos de contacto. Hay campos inválidos o incompletos.',
                            ),
                        )
                    }
                }
            } else {
                setFormError('')
            }
        })
        return () => subscription.unsubscribe()
    }, [form, t])

    // Update form values when clientProfile changes
    useEffect(() => {
        if (clientProfile) {
            console.log('Setting form values with client profile:', clientProfile)
            
            // Parsear dirección si existe
            const parsedAddress = parseAddress(clientProfile.address)

            const clientProfileDefaults = {
                clientName: clientProfile.name || '',
                clientLastName: clientProfile.lastName || '',
                clientPhone: clientProfile.phone || '',
                clientEmail: clientProfile.email || '',
                clientAddress:
                    parsedAddress.street &&
                    parsedAddress.streetNumber &&
                    parsedAddress.city &&
                    parsedAddress.postalCode &&
                    parsedAddress.province &&
                    parsedAddress.country
                        ? ''
                        : clientProfile.address || '',
                clientStreet: parsedAddress.street,
                clientStreetNumber: parsedAddress.streetNumber,
                clientCity: parsedAddress.city,
                clientPostalCode: parsedAddress.postalCode,
                clientProvince: parsedAddress.province,
                clientCountry: parsedAddress.country,
            }

            // Selección de mascota por petIndex
            let selectedPet = undefined
            if (typeof petIndex === 'number' && !isNaN(petIndex) && clientProfile.pets[petIndex]) {
                selectedPet = clientProfile.pets[petIndex]
            } else {
                selectedPet = clientProfile.pets[0]
            }
            form.reset({
                ...form.getValues(),
                ...clientProfileDefaults,
                ...(selectedPet && {
                    pet: {
                        name: selectedPet.name,
                        breed: selectedPet.breed,
                        weight: selectedPet.weight,
                        age: selectedPet.age,
                        isNeutered: selectedPet.isNeutered,
                        personality: selectedPet.personality,
                        size: selectedPet.size,
                        sex: selectedPet.sex,
                    },
                }),
            })
        }
    }, [clientProfile, form, petIndex])

    const handleDateTimeSelect = (date: Date) => {
        form.setValue('date', date)
        setDateTimeError('')
    }

    const handleDriverServiceChange = (newDriverService: DriverService | undefined) => {
        setDriverService(newDriverService)
    }

    const handleCheckInTimeChange = (time: string) => {
        setCheckInTime(time)
        // Also update the form.time value for hair salon bookings
        form.setValue('time', time)
    }

    console.log('form.watch', form.watch())
    const handleSubmit = async (values: FormValues) => {
        console.log('values', values)
        if (!values.date || !values.time) {
            setFormError(
                t(
                    'peluqueria.booking.errors.dateTime.required',
                    'Por favor, selecciona una fecha y hora para el servicio.',
                ),
            )
            return
        }
        setFormError('')
        setIsSubmitting(true)
        setHasBackendError(false)

        // Build additional services array
        const additionalServices = []

        // Add hairdressing service
        additionalServices.push({
            type: 'hairdressing' as const,
            petIndex: 0,
            services: values.services as HairdressingServiceType[],
        })

        // Add driver service if selected
        if (driverService) {
            additionalServices.push(driverService)
        }

        // Concatenar campos de dirección si no hay dirección guardada
        let finalAddress = values.clientAddress || ''
        if (!finalAddress && values.clientStreet) {
            const addressParts = [
                values.clientStreet,
                values.clientStreetNumber,
                values.clientCity,
                values.clientPostalCode,
                values.clientProvince,
                values.clientCountry,
            ].filter(Boolean)
            finalAddress = addressParts.join(', ')
        }

        const newReservation: Omit<HairSalonReservation, 'id' | 'totalPrice' | 'status' | 'paymentStatus' | 'source'> =
            {
                type: 'peluqueria',
                date: format(values.date, 'yyyy-MM-dd'),
                time: values.time,
                client: {
                    name: values.clientName,
                    lastName: values.clientLastName,
                    phone: values.clientPhone,
                    email: values.clientEmail,
                    address: finalAddress,
                    pets: clientProfile?.pets || [],
                    classification: clientProfile?.classification || 'normal',
                    language: (i18n.language === 'en' ? 'en' : 'es') as 'en' | 'es',
                },
                pet: values.pet,
                additionalServices,
                clientId: userId || '',
            }
        console.log('New reservation:', { newReservation })

        try {
            const savedReservation = await addReservation(newReservation)
            console.log('Saved reservation:', { savedReservation })
            localStorage.setItem('bookedReservation', JSON.stringify(savedReservation))
            navigate(`/booking/confirmation/${savedReservation.id}`)
        } catch (error) {
            console.error('Error saving reservation:', error)
            setHasBackendError(true)
            setFormError(
                t(
                    'booking.errors.saveReservation',
                    'Hubo un error al guardar la reserva. Por favor, inténtalo de nuevo.',
                ),
            )
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <div className='mx-auto max-w-4xl p-4'>
            <div className='absolute right-4 top-4 z-50 flex items-center gap-2'>
                <Link to='/perfil'>
                    <Button
                        variant='outline'
                        size='sm'
                        className='flex h-[34px] items-center justify-center bg-accent px-3 font-semibold hover:bg-accent/80'
                    >
                        {t('booking.myReservations', 'Mis Reservas')}
                    </Button>
                </Link>
                <LanguageSwitchButton />
            </div>
            <img src='/dondersteen-logo.png' alt='Dondersteen Logo' className='mx-auto mb-8 mt-8 h-24' />
            <div className='lg:col-span-2'>
                <div className='space-y-8'>
                    <h1 className='text-2xl font-bold sm:text-3xl'>
                        {t('peluqueria.booking.title', 'Reserva de Peluquería')}
                    </h1>
                    <DaycareBanner />

                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(handleSubmit)} className='space-y-8'>
                            <PetDetailsForm
                                form={form}
                                petIndex={0}
                                onRemove={() => {}}
                                petPrice={0}
                                singlePet
                                removePricing
                            />
                            <ServicesSection
                                form={form}
                                driverService={driverService}
                                onDriverServiceChange={handleDriverServiceChange}
                                checkInTime={checkInTime}
                                onCheckInTimeChange={handleCheckInTimeChange}
                                selectedDate={form.watch('date')}
                            />

                            <FormField
                                control={form.control}
                                name='date'
                                render={({ field }) => (
                                    <FormItem className='space-y-4'>
                                        <FormControl>
                                            <PeluqueriaAvailabilityCalendar
                                                onSelect={handleDateTimeSelect}
                                                selectedDate={field.value}
                                            />
                                        </FormControl>
                                        <FormMessage className='text-sm' />
                                    </FormItem>
                                )}
                            />

                            <ClientDetails form={form} clientProfile={clientProfile} userIdFromUrl={searchParams.get('userId')} />

                            {dateTimeError && (
                                <Alert variant='destructive'>
                                    <AlertCircle className='h-4 w-4' />
                                    <AlertTitle>Error</AlertTitle>
                                    <AlertDescription className='text-sm'>{dateTimeError}</AlertDescription>
                                </Alert>
                            )}

                            <SubmitSection
                                formError={formError}
                                hasBackendError={hasBackendError}
                                isSubmitting={isSubmitting}
                                globalConfig={globalConfig}
                            />
                        </form>
                    </Form>
                </div>
            </div>
        </div>
    )
}
